import {
  AddFamilyMember,
  AddInsurancePage,
  BenefitPage,
  CalculatingQuantities,
  CancelOrderForm,
  DeliveryAddressesPage,
  DeliverySelection,
  DosageCheck,
  FamilyMemberAdded,
  FamilyMemberBenefits,
  FamilyMemberCompleteProfile,
  FamilyMemberMedicalInfo,
  FamilyMemberRequest,
  InstructionalVideosPage,
  LeftoverMeds,
  MedicationSelection,
  NotificationCenter,
  OrderEarlyForm,
  OrderEarlySubmit,
  OnboardingLandingPage as OrganicOnboardingLandingPage,
  PriceDetails,
  ProgynyWizardIntro,
  ProgynyWizardIntro2,
  ProgynyWizardIntro3,
  QuantitiesReview,
  QuantityChange,
  QuestionPage,
  StimStartDate,
  SupportPage,
  TransferSuccess,
  TriggerDate,
} from '@alto/features';
import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import CSSTransition from 'react-transition-group/CSSTransition';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import { SEGMENT_WRITE_KEY } from '~shared/config';
import {
  CREATE_ACCOUNT,
  FERTILITY_SUPPORT,
  INSURANCE_FOUND,
  INSURANCE_INPUT,
  INSURANCE_INPUT_MANUAL,
  INSURANCE_INPUT_PHOTO,
  MEDICAL_INFO,
  ONBOARD_SUCCESS,
  PHI_AUTHORIZATION,
  VERIFY,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { ROUTE_TO_URL } from '~shared/features/onboarding/helpers/routeToPath';
import { getAddFamilyMemberRelationship } from '~shared/selectors/ui/addFamilyMember';
import { useSelectorShared } from '~shared/store';
import { Page } from './components/Page';
import { ReplyForm } from './features/alto-assistant/components/support-cases/ReplyForm';
import SupportCase from './features/alto-assistant/components/support-cases/SupportCase';
import { AppModals } from './features/app/components/AppModals';
import { Banner } from './features/app/components/Banner';
import { DashboardNavbar } from './features/app/components/DashboardNavbar';
import { Footer } from './features/app/components/Footer';
import { ImposterBanner } from './features/app/components/ImposterBanner';
import { NoMatch } from './features/app/components/NoMatch';
import { isAddFamilyMemberRoute, isRouteWithoutFooter } from './features/app/components/routeHelper';
import { AuthRequired } from './features/auth/components/AuthRequired';
import { PhotoIDUpload } from './features/checkout/components/PhotoIDUpload';
import { DeliveriesList, OrderDetails } from './features/deliveries';
import { DeliveryConfirmedPage } from './features/deliveries/components/DeliveryConfirmedPage';
import { OrderSuccess as LegacyOrderSuccess } from './features/deliveries/components/OrderSuccess';
import { OrderSuccess, StoreCategory, StoreProduct, StoreProducts } from './features/essentials';
import { Homescreen } from './features/homescreen/components/Homescreen';
import {
  CommsConfigPage as HubReferralInfoRequestCommsConfigPage,
  CompletePage as HubReferralInfoRequestCompletePage,
  InsuranceInputManualPage as HubReferralInfoRequestInsuranceInputManualPage,
  InsuranceInputPage as HubReferralInfoRequestInsuranceInputPage,
  InsuranceInputPhotoPage as HubReferralInfoRequestInsuranceInputPhotoPage,
  InsurancePage as HubReferralInfoRequestInsurancePage,
  LandingPage as HubReferralInfoRequestLandingPage,
  PatientAuthorizationPage as HubReferralInfoRequestPatientAuthorizationPage,
} from './features/info-request/components';
import { InsurancePhotoFormPage } from './features/insurances/components/InsurancePhotoFormPage';
import { ProgynyLandingPageContainer as ProgynyLandingPage } from './features/onboarding/components/ProgynyLandingPageContainer';
import { FertilitySupport } from './features/onboarding/components/redesign/FertilitySupport';
import { OnboardingCreateAccountPage } from './features/onboarding/components/redesign/OnboardingCreateAccountPage';
import OnboardingInsuranceFound from './features/onboarding/components/redesign/OnboardingInsuranceFoundContainer';
import { OnboardingInsuranceInputContainer as OnboardingInsuranceInput } from './features/onboarding/components/redesign/OnboardingInsuranceInputContainer';
import OnboardingInsuranceInputManual from './features/onboarding/components/redesign/OnboardingInsuranceInputManual';
import OnboardingInsuranceInputPhoto from './features/onboarding/components/redesign/OnboardingInsuranceInputPhoto';
import OnboardingMedicalInfo from './features/onboarding/components/redesign/OnboardingMedicalInfo';
import OnboardingPhiAuthorization from './features/onboarding/components/redesign/OnboardingPhiAuthorization';
import OnboardingProviderSuccess from './features/onboarding/components/redesign/OnboardingProviderSuccessContainer';
import { OnboardingVerifyContainer as OnboardingVerify } from './features/onboarding/components/redesign/OnboardingVerifyContainer';
import { Profile } from './features/profile/components/Profile';
import { ProfileAutoRefills } from './features/profile/components/ProfileAutoRefills';
import { ProfileContactInfo } from './features/profile/components/ProfileContactInfo';
import { ProfileContactSupport } from './features/profile/components/ProfileContactSupport';
import { ProfilePaymentMethods } from './features/profile/components/ProfilePaymentMethods';
import { SendPrescriptionOptions } from './features/profile/components/SendPrescriptionOptions';
import { UserProfile } from './features/profile/components/UserProfile';
import { RateShipmentPage } from './features/rate-shipment/RateShipmentPage';
import { NotMe } from './features/subscription-management/NotMe';
import { SubscriptionsPage } from './features/subscription-management/SubscriptionsPage';
import { CommsVerificationStatusContainer } from './features/subscription-management/components/CommsVerificationStatusContainer';
import { UnauthCommsVerificationStatus } from './features/subscription-management/components/UnauthCommsVerificationStatus';
import { IdentityVerificationPage } from './features/subscription-management/verification/IdentityVerificationPage';
import { TransferPrescriptions } from './features/transfers/components/TransferPrescriptions';
import WebLandingPageTracker from './features/web-landing-page-tracker/components/WebLandingPageTrackerContainer';
import { WhatMedicationQuestion } from '~web/features/alto-assistant/components/medications/WhatMedicationQuestion';
import { MessageForm } from '~web/features/alto-assistant/components/messaging/MessageForm';
import { Welcome } from '~web/features/auth/components/Welcome';
import { CheckoutContainer } from '~web/features/checkout/components/CheckoutContainer';
import { DrugInfo } from '~web/features/my-meds/components/DrugInfo';
import { MedDetailsWrapper } from '~web/features/my-meds/components/MedDetailsWrapper';
import { MedListWrapper } from '~web/features/my-meds/components/MedListWrapper';
import { ContactRequestedPage } from '~web/features/onboarding/components/ContactRequestedPage';
import { SignupPage } from '~web/features/onboarding/components/SignupPage';

export const App = () => {
  const location = useLocation();

  useEffect(() => {
    // @ts-expect-error TS(2339): Property 'analytics' does not exist on type 'Window & typeof globalThis'.
    const { analytics } = window;
    if (analytics?.load && SEGMENT_WRITE_KEY) {
      analytics.load(SEGMENT_WRITE_KEY);
    } else {
      console.error('Not able to load analytics:', {
        SEGMENT_WRITE_KEY,
        analytics,
      });
    }
  }, []);

  // relationship is set in redux when adding a family member, hide footer in that flow
  const relationship = useSelectorShared(getAddFamilyMemberRelationship);
  const isAddingFamilyMember = isAddFamilyMemberRoute(location.pathname, relationship);
  const shouldHideFooter = isRouteWithoutFooter(location.pathname) || isAddingFamilyMember;
  const footer = shouldHideFooter ? null : <Footer />;

  // React 19 removed findDOMNode; react-transition-group needs a `nodeRef`.
  // Per the library docs we need a *fresh* ref per transition key (location.pathname)
  // so the incoming and outgoing children measure independent DOM nodes.
  const nodeRefs = useRef(new Map<string, React.RefObject<HTMLDivElement | null>>());
  const getNodeRef = (key: string) => {
    let ref = nodeRefs.current.get(key);
    if (!ref) {
      ref = React.createRef<HTMLDivElement>();
      nodeRefs.current.set(key, ref);
    }
    return ref;
  };
  const routeNodeRef = getNodeRef(location.pathname);

  return (
    <Page footer={footer}>
      <ImposterBanner>
        <DashboardNavbar />
        <Banner />
        <AppModals /> {/* Deprecated use action sheet instead */}
        {/* The incoming route fades in (absolutely positioned) on top of the outgoing route, */}
        {/* which stays at full opacity until the fade completes — then the outgoing unmounts. */}
        {/* No mid-transition gap, no white flicker. CSS lives in main.scss. */}
        <TransitionGroup className="route-transition-group">
          <CSSTransition
            key={location.pathname}
            nodeRef={routeNodeRef}
            classNames="fade"
            timeout={150}
          >
            <div ref={routeNodeRef}>
              <Outlet />
            </div>
          </CSSTransition>
        </TransitionGroup>
      </ImposterBanner>
    </Page>
  );
};

// this looks like a react-component, but it's not used as one
export const WebRoutes = () => (
  <Routes>
    {/* Begin Hub Referral Info Request routes */}
    {/* only use paths starting with info for HubReferral info Pages  */}
    <Route
      path="/info/:externalUUID/start/:hubReferralID"
      element={<HubReferralInfoRequestLandingPage />}
    />
    <Route
      path="/info/:externalUUID/sign-auth/:hubReferralID"
      element={<HubReferralInfoRequestPatientAuthorizationPage />}
    />
    <Route
      path="/info/:externalUUID/insurance-input/:hubReferralID"
      element={<HubReferralInfoRequestInsuranceInputPage />}
    />
    <Route
      path="/info/:externalUUID/insurance-input-photo/:hubReferralID"
      element={<HubReferralInfoRequestInsuranceInputPhotoPage />}
    />
    <Route
      path="/info/:externalUUID/insurance-input-manual/:hubReferralID"
      element={<HubReferralInfoRequestInsuranceInputManualPage />}
    />
    <Route
      path="/info/:externalUUID/insurance/:hubReferralID"
      element={<HubReferralInfoRequestInsurancePage />}
    />
    <Route
      path="/info/:externalUUID/comms-config/:hubReferralID"
      element={<HubReferralInfoRequestCommsConfigPage />}
    />
    <Route
      path="/info/:externalUUID/complete/:hubReferralID"
      element={<HubReferralInfoRequestCompletePage />}
    />
    {/* End Hub Referral Info Request routes */}

    <Route
      path="/start/progyny/:tempPassword"
      element={<ProgynyLandingPage />}
    />
    <Route
      path="/start/:tempPassword"
      element={<WebLandingPageTracker />}
    />
    <Route
      path="/subscriptions/:uuid"
      element={<SubscriptionsPage />}
    />
    <Route
      path="/"
      element={<App />}
    >
      <Route
        index
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />
      <Route
        path="/rate-shipment/:uuid"
        element={<RateShipmentPage />}
      />

      <Route
        path="/orders/cancel"
        element={<CancelOrderForm />}
      />

      <Route
        path="/deliveries/cancel"
        element={
          <Navigate
            to="/orders/cancel"
            replace
          />
        }
      />

      <Route
        path="/"
        element={<AuthRequired />}
      >
        <Route
          path="/orders"
          element={<DeliveriesList />}
        />

        <Route
          path="/orders/:orderID"
          element={<OrderDetails />}
        />

        <Route
          path="/prescriptions"
          element={<MedListWrapper />}
        />

        <Route
          path="/prescriptions/:primaryPrescriptionId"
          element={<MedDetailsWrapper />}
        />

        <Route
          path="/prescriptions/:prescriptionID/order-early"
          element={<OrderEarlyForm />}
        />

        <Route
          path="/prescriptions/:prescriptionID/order-early/submit"
          element={<OrderEarlySubmit />}
        />

        <Route
          path="/prescriptions/:prescriptionID/change-quantity"
          element={<QuantityChange />}
        />

        <Route
          path="/prescriptions/:rx_number/drug_info"
          element={<DrugInfo />}
        />
        {/* /messages displayed a read only thread of wundercom messages to the patient */}
        {/* redirect to notifications instead */}
        <Route
          path="/messages"
          element={
            <Navigate
              to="/notifications"
              replace
            />
          }
        />
        <Route
          path="/notifications"
          element={<NotificationCenter />}
        />
        {/* Both /messages_inbox and /help route to alto assistant support page */}
        {/* messages_inbox is set on the backend for deeplinking purposes on the mobile app */}
        <Route
          path="/messages_inbox"
          element={
            <Navigate
              to="/help"
              replace
            />
          }
        />
        <Route
          path="/help"
          element={<SupportPage />}
        />

        <Route
          path="/help/message"
          element={<MessageForm />}
        />

        <Route
          path="/help/deliveries"
          element={<DeliverySelection />}
        />

        <Route
          path="/help/deliveries/price-details/:shipmentID"
          element={<PriceDetails />}
        />

        <Route
          path="/help/medications"
          element={<MedicationSelection />}
        />

        <Route
          path="/help/support/:supportCaseID"
          element={<SupportCase />}
        />

        <Route
          path="/help/support/:supportCaseID/reply"
          element={<ReplyForm />}
        />

        <Route
          path="/help/medication_questions"
          element={<WhatMedicationQuestion />}
        />

        <Route
          path="/me"
          element={<Profile />}
        />

        <Route
          path="/app/homescreen/insurances/:userID"
          element={<AddInsurancePage />}
        />

        <Route
          path="/me/:userID/add-benefit"
          element={<AddInsurancePage />}
        />

        <Route
          path="/me/auto-refills"
          element={<ProfileAutoRefills />}
        />

        <Route
          path="me/contact-info"
          element={<ProfileContactInfo />}
        />

        <Route
          path="/me/payments"
          element={<ProfilePaymentMethods />}
        />

        <Route
          path="/me/family_member"
          element={<AddFamilyMember />}
        />

        <Route
          path="/me/family_member/added"
          element={<FamilyMemberAdded />}
        />

        <Route
          path="/me/:userID/medical-info"
          element={<FamilyMemberMedicalInfo />}
        />

        <Route
          path="/me/:userID/add-benefits"
          element={<FamilyMemberBenefits />}
        />

        <Route
          path="/me/:userID/complete-profile"
          element={<FamilyMemberCompleteProfile />}
        />

        <Route
          path="/me/family_member/request"
          element={<FamilyMemberRequest />}
        />

        <Route
          path="/me/addresses"
          element={<DeliveryAddressesPage />}
        />

        <Route
          path="/me/faq"
          element={<NoMatch />}
        />

        <Route
          path="/me/faq/:faqKey"
          element={<QuestionPage />}
        />

        <Route
          path="/me/contact-support"
          element={<ProfileContactSupport />}
        />

        <Route
          path="/me/send-prescription-options"
          element={<SendPrescriptionOptions />}
        />

        <Route
          path="/me/fertility-videos"
          element={<InstructionalVideosPage />}
        />

        <Route
          path="/me/:userID/benefits/photos"
          element={<InsurancePhotoFormPage />}
        />

        <Route
          path="/me/:userID/benefits"
          element={<BenefitPage />}
        />

        <Route
          path="/me/:userID"
          element={<UserProfile />}
        />

        <Route
          path="/checkout"
          element={<CheckoutContainer />}
        />

        <Route
          path="/checkout/success/:shipmentID"
          element={<LegacyOrderSuccess />}
        />

        <Route
          path="/checkout/photo-id-upload"
          element={<PhotoIDUpload />}
        />

        <Route path="/checkout/progyny-wizard">
          <Route
            index
            element={
              <Navigate
                to="intro-1"
                replace
              />
            }
          />
          <Route
            path="calculating-quantities"
            element={<CalculatingQuantities />}
          />
          <Route
            path="dosage-check"
            element={<DosageCheck />}
          />
          <Route
            path="intro-1"
            element={<ProgynyWizardIntro />}
          />
          <Route
            path="intro-2"
            element={<ProgynyWizardIntro2 />}
          />
          <Route
            path="intro-3"
            element={<ProgynyWizardIntro3 />}
          />
          <Route
            path="leftover-meds"
            element={<LeftoverMeds />}
          />
          <Route
            path="quantities-review"
            element={<QuantitiesReview />}
          />
          <Route
            path="start-date"
            element={<StimStartDate />}
          />
          <Route
            path="trigger-date"
            element={<TriggerDate />}
          />
        </Route>

        <Route
          path="/transfer"
          element={<TransferPrescriptions />}
        />

        <Route
          path="/transfer/success"
          element={<TransferSuccess />}
        />

        <Route
          path="/home"
          element={<Homescreen />}
        />

        <Route
          path="/store"
          element={<StoreProducts />}
        />
        <Route
          path="/store/:id"
          element={<StoreProduct />}
        />
        <Route
          path="/store/order-success/:shipmentID"
          element={<OrderSuccess />}
        />
        <Route
          path="/store/category/:categoryName"
          element={<StoreCategory />}
        />
      </Route>

      {/* Begin onboarding redesign routes */}
      {/* Verify is actually no longer explicitly part of the flow (it's a modal/drawer on the landing page), however
      this still exists in case the user is dropped into another onboarding page without verifying, they will redirect
      to this page */}
      <Route
        path={ROUTE_TO_URL[VERIFY]}
        element={<OnboardingVerify />}
      />

      <Route element={<AuthRequired isUsingNextParam={false} />}>
        <Route
          path={ROUTE_TO_URL[INSURANCE_FOUND]}
          element={<OnboardingInsuranceFound />}
        />

        <Route
          path={ROUTE_TO_URL[INSURANCE_INPUT]}
          element={<OnboardingInsuranceInput />}
        />

        <Route
          path={ROUTE_TO_URL[INSURANCE_INPUT_MANUAL]}
          element={<OnboardingInsuranceInputManual />}
        />

        <Route
          path={ROUTE_TO_URL[INSURANCE_INPUT_PHOTO]}
          element={<OnboardingInsuranceInputPhoto />}
        />

        <Route
          path={ROUTE_TO_URL[MEDICAL_INFO]}
          element={<OnboardingMedicalInfo />}
        />

        <Route
          path={ROUTE_TO_URL[PHI_AUTHORIZATION]}
          element={<OnboardingPhiAuthorization />}
        />

        <Route
          path={ROUTE_TO_URL[CREATE_ACCOUNT]}
          element={<OnboardingCreateAccountPage />}
        />

        <Route
          path={ROUTE_TO_URL[FERTILITY_SUPPORT]}
          element={<FertilitySupport />}
        />
        <Route
          path={ROUTE_TO_URL[ONBOARD_SUCCESS]}
          element={<OnboardingProviderSuccess />}
        />
      </Route>
      {/* End onboarding redesign routes */}

      {/* Kept for backwards compatibility, make sure this is after the other onboarding routes to get matched last */}
      <Route
        path="onboard/:tempPassword"
        element={
          <Navigate
            to="onboard/verify/:tempPassword"
            replace
          />
        }
      />

      <Route
        path="/login"
        element={<Welcome />}
      />

      <Route
        path="/signup"
        element={<SignupPage />}
      />

      {/* Redirect legacy password reset route to Auth0 login */}
      <Route
        path="/forgot_password"
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />

      <Route
        path="/delivery_confirmed"
        element={<DeliveryConfirmedPage />}
      />

      <Route
        path="/communications/:verificationType/not_me"
        element={<NotMe />}
      />

      <Route
        path="/communications/verify_identity/:commsChannel"
        element={<IdentityVerificationPage />}
      />

      <Route
        path="/communications/verify_identity/:commsChannel/:verificationStatus"
        element={<UnauthCommsVerificationStatus />}
      />

      <Route
        path="/communications/verify/:commsChannel/:token"
        element={<CommsVerificationStatusContainer />}
      />

      <Route
        path="/contact_requested"
        element={<ContactRequestedPage />}
      />

      <Route
        path="/patient-onboarding"
        element={<OrganicOnboardingLandingPage />}
      />

      <Route
        path="*"
        element={<NoMatch />}
      />
    </Route>
  </Routes>
);
